/**
 * fetch для клиента API: при 401 обновляет access-токен по refresh-cookie и повторяет запрос
 * один раз; если обновить нельзя — сессия завершается. 403 MFA_REQUIRED переводит сессию
 * на экран второго фактора. Сетевые сбои превращаются в ApiError NETWORK_ERROR.
 */
import { networkError, type HttpFetch } from './http';

export interface AuthFetchDeps {
  getAccessToken: () => string | null;
  /** Обновить токен (single-flight); null — сессии больше нет. */
  refresh: () => Promise<string | null>;
  onMfaRequired: () => void;
  /** Токен отклонён и после обновления — завершить сессию. */
  onSessionRejected: (code: string) => void;
  baseFetch?: HttpFetch;
}

const REFRESHABLE_CODES = new Set(['TOKEN_EXPIRED', 'TOKEN_INVALID', 'UNAUTHORIZED', 'HTTP_401']);

async function errorCodeOf(response: Response): Promise<string> {
  try {
    const body = (await response.clone().json()) as { error?: { code?: unknown } } | null;
    return typeof body?.error?.code === 'string' ? body.error.code : `HTTP_${response.status}`;
  } catch {
    return `HTTP_${response.status}`;
  }
}

function isAbort(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: string }).name === 'AbortError';
}

export function createAuthFetch(deps: AuthFetchDeps): HttpFetch {
  const baseFetch: HttpFetch = deps.baseFetch ?? ((request) => fetch(request));
  const send = async (request: Request): Promise<Response> => {
    try {
      return await baseFetch(request);
    } catch (error) {
      if (isAbort(error)) throw error;
      throw networkError();
    }
  };

  return async (request: Request): Promise<Response> => {
    const retryRequest = request.clone();
    const response = await send(request);

    if (response.status === 401) {
      const code = await errorCodeOf(response);
      const hadToken = request.headers.has('Authorization') || deps.getAccessToken() !== null;
      if (!hadToken) return response;
      if (!REFRESHABLE_CODES.has(code)) {
        deps.onSessionRejected(code);
        return response;
      }
      const token = await deps.refresh();
      if (!token) return response;
      retryRequest.headers.set('Authorization', `Bearer ${token}`);
      const retried = await send(retryRequest);
      if (retried.status === 401) deps.onSessionRejected(await errorCodeOf(retried));
      else if (retried.status === 403 && (await errorCodeOf(retried)) === 'MFA_REQUIRED') deps.onMfaRequired();
      return retried;
    }

    if (response.status === 403 && (await errorCodeOf(response)) === 'MFA_REQUIRED') deps.onMfaRequired();
    return response;
  };
}
